import { Alert, Button, Card, Form, Grid, Input, Message, Select, Space } from "@arco-design/web-react"
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { InputNumber } from "@/components/Numeric";
import { Currency } from "@/components/Numeric";
import { EnumSelect } from "@/components/Enum";
import { ReferenceTypeDef } from "@/enums/purchasing";
import { SOAPI, SOFulfillmentAPI, SOPOAPI } from "@/apis/sales";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import _ from "lodash";
import InventorySelect from "@/components/InventorySelect";
import { formatDate } from "@/utils";

const FormItem = Form.Item;
const Row = Grid.Row;
const Col = Grid.Col;

export const SOFulfillmentEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [form] = Form.useForm();
    const [soItems, setSOItems] = useState();
    const [items, setItems] = useState();
    const [saving, setSaving] = useState(false);

    const onLotSelect = (field, lot) => {
        form.setFieldValue(`${field}.warehouseId`, lot.warehouseId);
        form.setFieldValue(`${field}.warehouseName`, lot.warehouseName);
        form.setFieldValue(`${field}.locationId`, lot.locationId);
        form.setFieldValue(`${field}.locationName`, lot.locationName);
        form.setFieldValue(`${field}.lot`, lot.lot);
        form.setFieldValue(`${field}.expiredDate`, lot.expiredDate ? formatDate(lot.expiredDate) : null);
    }

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const values = await form.validate();
            const allFields = form.getFieldsValue();
            const missingInventory = _.filter(allFields.items, (item) => !item.sopoId && !item.warehouseId);
            if (missingInventory.length > 0) {
                Message.warning("Please select inventory for all items before submitting");
                setSaving(false);
                return;
            }
            values.items = _.map(values.items, (item) => _.omit(item, ['inventoryKey', 'warehouseLocation']));
            await SOFulfillmentAPI.update(values.id, values);
            Message.success("SO fulfillment updated successfully");
            navigate(`/workspace/sales/so-fulfillment/${params.fulfillmentId}`);
        } catch (error) {
            Message.error(error.message);
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        const init = async () => {
            const info = await SOFulfillmentAPI.get(params.fulfillmentId);
            const so = await SOAPI.get(info.soId);

            setSOItems(so.items);
            let sortedItems = _.sortBy(so.items, "itemName");
            sortedItems = _.filter(sortedItems, (item) => item.itemType === 1);
            setItems(_.map(sortedItems, (item) => ({
                label: <span>{item.itemName}(<Currency value={item.rate} />)</span>,
                value: item.id
            })));

            _.forEach(info.items, (item) => {
                if (item.warehouseId || item.locationId || item.lot) {
                    item.inventoryKey = {
                        warehouseId: item.warehouseId,
                        locationId: item.locationId,
                        lot: item.lot
                    };
                }
            });

            const sopoIds = _.uniq(_.map(_.filter(info.items, 'sopoId'), 'sopoId'));
            const sopoMap = {};
            for (const sopoId of sopoIds) {
                const sopo = await SOPOAPI.get(sopoId);
                sopoMap[sopoId] = sopo.pickOrderNo;
            }
            _.forEach(info.items, (item) => {
                if (item.sopoId) {
                    item.sopoNo = sopoMap[item.sopoId];
                }
            });

            form.setFieldsValue(info);
        };
        init();
    }, [params, form])

    return (
        <>
            <Alert style={{ marginBottom: 10 }} content="Psst: Items from Pick Orders can not be changed on fulfillment page. To make any changes of items from Pick Orders, go back to Pick Order, make the changes, and refulfill the Pick Order." />

            <Card title="Edit SO Fulfillment" extra={
                <Space>
                    <Button type="primary" loading={saving} onClick={() => save()}>Save</Button>
                    <Button onClick={() => navigate(`/workspace/sales/so-fulfillment/${params.fulfillmentId}`)}>Cancel</Button>
                </Space>
            }>
                <Form form={form} colon
                    labelCol={{ span: 0 }}
                    wrapperCol={{ span: 24 }}>
                    <FormItem>
                        <Row gutter={8}>
                            <Col span={4}>
                                <FormItem field="id" hidden>
                                    <Input />
                                </FormItem>
                                <FormItem field="soId" hidden>
                                    <Input />
                                </FormItem>
                                <FormItem label="SO#" field="soNo" labelCol={{ span: 6 }}>
                                    <Input readOnly />
                                </FormItem>
                            </Col>

                            <Col span={6}>
                                <FormItem label="SO Due Date" field="soDueDate" labelCol={{ span: 12 }}>
                                    <Input readOnly />
                                </FormItem>
                            </Col>
                            <Col span={6}>
                                <FormItem label="SO Creation Date" field="soCreateDate" labelCol={{ span: 12 }}>
                                    <Input readOnly />
                                </FormItem>
                            </Col>
                            <Col span={8}>
                                <FormItem label="Fulfillment Date" field="fulfillmentDate" labelCol={{ span: 10 }}>
                                    <Input readOnly />
                                </FormItem>
                            </Col>
                        </Row>
                        <Row gutter={8}>
                            <Col span={12}>
                                <FormItem label="Reference">
                                    <Form.List field="references">
                                        {(fields, { add, remove }) =>
                                            <>
                                                {fields.map((item, index) => {
                                                    return (
                                                        <FormItem key={item.key} style={{ marginBottom: 0 }}>
                                                            <Space className="form-item-row">
                                                                <FormItem field={item.field + ".type"}>
                                                                    <EnumSelect enumObj={ReferenceTypeDef} order="label" style={{ width: 200 }} showSearch />
                                                                </FormItem>
                                                                <FormItem field={item.field + ".number"}>
                                                                    <Input />
                                                                </FormItem>
                                                                <Button type="text" status="danger" icon={<IconDelete />} onClick={() => remove(index)}></Button>
                                                            </Space>
                                                        </FormItem>
                                                    )
                                                })}
                                                <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Reference</Button>
                                            </>
                                        }
                                    </Form.List>
                                </FormItem>
                            </Col>
                        </Row>
                    </FormItem>
                    <FormItem label="Items" labelCol={{ span: 1 }}>
                        <Form.List field="items">
                            {(fields, { add, remove }) =>
                                <>
                                    <table width="100%">
                                        <thead>
                                            <tr>
                                                <th>Line#</th>
                                                <th>Item Number</th>
                                                <th>Item Name</th>
                                                <th>Description</th>
                                                <th>PKS#</th>
                                                <th width="100">SO Qty</th>
                                                <th width="100">Qty to Fulfill</th>
                                                <th>UOM</th>
                                                <th width="300">Location|Lot#|Expiration</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fields.map((item, index) => {
                                                return (
                                                    <tr key={index}>
                                                        <td>
                                                            <span>#{index + 1}</span>
                                                            <FormItem field={item.field + ".id"} hidden>
                                                                <Input />
                                                            </FormItem>
                                                        </td>
                                                        <td>
                                                            <FormItem field={item.field + ".itemId"} hidden>
                                                                <Input />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".itemNo"} noStyle>
                                                                <Input readOnly />
                                                            </FormItem>
                                                        </td>
                                                        <td>
                                                            {form.getFieldValue(`${item.field}.sopoId`) == null ?
                                                                <FormItem field={item.field + ".soItemId"} noStyle>
                                                                    <Select options={items}
                                                                        showSearch
                                                                        filterOption={(inputValue, option) => option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0}
                                                                        triggerProps={{
                                                                            autoAlignPopupWidth: false,
                                                                            autoAlignPopupMinWidth: true
                                                                        }}
                                                                        onChange={(val) => {
                                                                            const soItem = _.find(soItems, o => o.id === val);
                                                                            form.setFieldValue(`${item.field}.itemId`, soItem.itemId);
                                                                            form.setFieldValue(`${item.field}.itemNo`, soItem.itemNo);
                                                                            form.setFieldValue(`${item.field}.description`, soItem.description);
                                                                            form.setFieldValue(`${item.field}.soQty`, soItem.quantity);
                                                                            form.setFieldValue(`${item.field}.uom`, soItem.uom);
                                                                        }} />
                                                                </FormItem>
                                                                :
                                                                <>
                                                                    <FormItem field={item.field + ".soItemId"} hidden>
                                                                        <Input />
                                                                    </FormItem>
                                                                    <FormItem field={item.field + ".itemName"} noStyle>
                                                                        <Input readOnly />
                                                                    </FormItem>
                                                                </>
                                                            }
                                                        </td>
                                                        <td>
                                                            <FormItem field={item.field + ".description"} noStyle>
                                                                <Input readOnly />
                                                            </FormItem>
                                                        </td>
                                                        <td>
                                                            <FormItem field={item.field + ".sopoNo"} noStyle>
                                                                <Input readOnly />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".sopoId"} hidden>
                                                                <Input readOnly />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".sopoItemId"} hidden>
                                                                <Input readOnly />
                                                            </FormItem>
                                                        </td>
                                                        <td>
                                                            <FormItem field={item.field + ".soQty"} noStyle>
                                                                <InputNumber readOnly />
                                                            </FormItem>
                                                        </td>
                                                        <td>
                                                            <FormItem field={item.field + ".qtyFulfilled"} required rules={[{ required: true }]} noStyle>
                                                                <InputNumber readOnly={form.getFieldValue(`${item.field}.sopoId`) != null} />
                                                            </FormItem>
                                                        </td>
                                                        <td>
                                                            <FormItem field={item.field + ".uom"} noStyle>
                                                                <Input readOnly />
                                                            </FormItem>
                                                        </td>
                                                        <td>
                                                            <FormItem field={item.field + ".warehouseId"} hidden>
                                                                <Input />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".warehouseName"} hidden>
                                                                <Input />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".locationId"} hidden>
                                                                <Input />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".locationName"} hidden>
                                                                <Input />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".lot"} hidden>
                                                                <Input />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".expiredDate"} hidden>
                                                                <Input />
                                                            </FormItem>
                                                            <FormItem field={item.field + ".inventoryKey"} noStyle>
                                                                <InventorySelect
                                                                    readOnly={form.getFieldValue(`${item.field}.sopoId`) != null}
                                                                    getItemId={() => {
                                                                        return form.getFieldValue(`${item.field}.itemId`);
                                                                    }}
                                                                    getQtyFulfilled={() => {
                                                                        return form.getFieldValue(`${item.field}.qtyFulfilled`);
                                                                    }}
                                                                    onSelect={(val) => onLotSelect(item.field, val)} />
                                                            </FormItem>
                                                        </td>
                                                        <td>
                                                            <FormItem hidden={form.getFieldValue(`${item.field}.sopoId`) != null}
                                                                noStyle={form.getFieldValue(`${item.field}.sopoId`) == null}>
                                                                <Button type="text" status="danger" icon={<IconDelete />}
                                                                    onClick={() => remove(index)}></Button>
                                                            </FormItem>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                    <FormItem>
                                        <Button type="text" icon={<IconPlus />} onClick={() => add()}>Add Item</Button>
                                    </FormItem>
                                </>
                            }
                        </Form.List>
                    </FormItem>
                </Form>
            </Card>
        </>
    )
}
